import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const FeatureContext = createContext();

export const useFeatures = () => {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
};

export const FeatureProvider = ({ children }) => {
  const { apiRequest, isAuthLoading, user, isAuthenticated } = useAuth();
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default feature configuration (fallback)
  const defaultFeatures = {
    microphones: { enabled: false },
    conversations: { enabled: false },
    cameras: { enabled: false },
    automations: { enabled: true },
    scenes: { enabled: true },
    devices: { enabled: true },
    smartLocks: { enabled: false },
    remoteAccess: { enabled: true },
    systemSettings: { enabled: true }
  };

  /**
   * Fetch feature configuration from backend
   */
  const fetchFeatureConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      // Only fetch if user is authenticated
      if (!isAuthenticated || !user) {
        setFeatures(defaultFeatures);
        setLoading(false);
        return;
      }

      // User is authenticated, fetch feature configuration
      const { data } = await apiRequest('/api/configuration/features');

      if (data.success) {
        setFeatures(data.data.features || defaultFeatures);
      } else {
        console.error('Failed to fetch feature configuration:', data.error);
        setFeatures(defaultFeatures);
        setError(data.error);
      }
    } catch (err) {
      console.error('Error fetching feature configuration:', err);
      setFeatures(defaultFeatures);
      setError('Failed to load feature configuration');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update feature configuration
   * @param {Object} updates - Feature updates
   */
  const updateFeatureConfig = async (updates) => {
    try {
      const { data } = await apiRequest('/api/configuration/features', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (data.success) {
        setFeatures(data.data.features || features);
        return { success: true };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error('Error updating feature configuration:', err);
      setError('Failed to update feature configuration');
      return { success: false, error: err.message };
    }
  };

  /**
   * Enable a specific feature
   * @param {string} featureName - Name of the feature to enable
   */
  const enableFeature = async (featureName) => {
    try {
      const { data } = await apiRequest(`/api/configuration/features/${featureName}/enable`, {
        method: 'POST'
      });

      if (data.success) {
        setFeatures(data.data.features || features);
        return { success: true };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error('Error enabling feature:', err);
      setError('Failed to enable feature');
      return { success: false, error: err.message };
    }
  };

  /**
   * Disable a specific feature
   * @param {string} featureName - Name of the feature to disable
   */
  const disableFeature = async (featureName) => {
    try {
      const { data } = await apiRequest(`/api/configuration/features/${featureName}/disable`, {
        method: 'POST'
      });

      if (data.success) {
        setFeatures(data.data.features || features);
        return { success: true };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error('Error disabling feature:', err);
      setError('Failed to disable feature');
      return { success: false, error: err.message };
    }
  };

  /**
   * Check if a feature is enabled
   * @param {string} featureName - Name of the feature to check
   * @returns {boolean} True if feature is enabled
   */
  const isFeatureEnabled = (featureName) => {
    return features[featureName]?.enabled || false;
  };

  /**
   * Get all enabled features
   * @returns {Array} Array of enabled feature names
   */
  const getEnabledFeatures = () => {
    return Object.keys(features).filter(featureName => 
      features[featureName]?.enabled === true
    );
  };

  /**
   * Get all disabled features
   * @returns {Array} Array of disabled feature names
   */
  const getDisabledFeatures = () => {
    return Object.keys(features).filter(featureName => 
      features[featureName]?.enabled === false
    );
  };

  // Load feature configuration when auth is complete
  useEffect(() => {
    // Only fetch features when:
    // 1. Auth loading is complete
    // 2. User is authenticated (or we know they're not)
    if (!isAuthLoading) {
      fetchFeatureConfig();
    }
  }, [isAuthLoading, isAuthenticated, user]);

  const value = {
    features,
    loading,
    error,
    fetchFeatureConfig,
    updateFeatureConfig,
    enableFeature,
    disableFeature,
    isFeatureEnabled,
    getEnabledFeatures,
    getDisabledFeatures
  };

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
};

export default FeatureContext;